<template>
  <div>
    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :master-tenant-filter.sync="masterTenantFilter"
          :master-tenant-options="masterTenantOptions"
          :tenant-filter.sync="tenantFilter"
          :tenant-options="tenantOptions"
          :profile-filter.sync="profileFilter"
          :profile-options="profileOptions"
          :online-filter.sync="onlineFilter"
        />
      </template>
    </filter-modal>

    <!-- Wizard -->
    <wizard
      :is-wizard-active.sync="isWizardActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      :is-clone.sync="isClone"
      :item-cloned="itemCloned"
      :id-item-original="idItemOriginal"
      @close-wizard="closeWizard"
      @refetch-data="refetchData"
      @isAdd-false="isAdd = false"
      :country-options="countryOptions"
      :type-alarm-options="typeAlarmOptions"
      :master-tenant-options="masterTenantOptions"
      :profile-options="profileOptions"
    />

    <b-modal
      id="reset-password"
      hide-header
      centered
      no-close-on-esc
      no-close-on-backdrop
      @show="url = ''"
    >
      <div>
        <p class="h3 text-capitalize">
          <span>{{ userEmail ? $t("users.reset_password") : $t("users.reset_password_no_email") }}</span>
        </p>
        <hr />
        <div class="text-center">
          <b-button
            variant="primary"
            @click="generateLink"
            :disabled="btnDisabled"
          >
            <span class="text-nowrap text-capitalize">{{ $t("users.generate_link") }}</span>
          </b-button>
          <b-button
            class="ml-2"
            variant="primary"
            @click="requestLinkemail"
            :disabled="btnDisabled"
            v-if="userEmail.includes('@') && !userEmail.match(/^[^@]*@\d+$/)"
          >
            <span class="text-nowrap text-capitalize">{{ $t("users.send_link_email") }}</span>
          </b-button>
          <div class="mt-2 mb-2" v-if="url">
            <b-input-group class="mt-3">
              <template #append>
                <b-input-group-text>
                  <div
                    v-clipboard:copy="`${url}`"
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                    style="cursor: pointer"
                  >
                    <feather-icon icon="LinkIcon" size="16" class="align-middle text-body" />
                  </div>
                </b-input-group-text>
              </template>
              <b-form-input v-model="url"></b-form-input>
            </b-input-group>
          </div>
        </div>
      </div>
      <template #modal-footer>
          <b-button
            variant="outline-secondary"
            class="text-capitalize float-right"
            :disabled="btnDisabled"
            @click="$bvModal.hide('reset-password')"
          >
            {{ $t("actions.cancel") }}
          </b-button>
      </template>
    </b-modal>

    <b-modal
      ref="loadQrUser"
      hide-header
      hide-footer
      no-close-on-esc
      no-close-on-backdrop
      centered
    >
      <AccountSettingMobileApp :user-id="selectedUserId" />
      <div class="d-flex justify-content-end">
        <b-button class="btn-sm" variant="secondary" @click="loadQrUser.hide()">
          Cerrar
        </b-button>
      </div>
    </b-modal>

    <!-- Table Container Card -->
    <b-card class="mx-2" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="4"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              data-cy="automatest-v-select-pagination-control"
            />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="8">
            <div class="d-flex align-items-center justify-content-end user-search-actions">
              <FacetedSearch
                :options="searchOptions"
                class="mr-1 user-search-input"
                @search="setFacetedFilters"
              />

              <b-button
                variant="secondary"
                @click="openWizard"
                v-if="$can('create', 'User')"
                data-cy="automatest-add-user-btn"
              >
                <span class="text-nowrap text-capitalize">{{
                  $t("actions.add") + " " + $tc("users.title", 1)
                }}</span>
              </b-button>

              <bulk-edit-button
                v-if="$can('update', 'User') && $can('bulk_update', 'User')"
                :is-bulk-edit-mode="isBulkEditMode"
                :selected-count="bulkSelectedCount"
                :has-selection="bulkHasSelection"
                :select-all-matching="bulkSelectAllMatching"
                @enable-multiselect="startBulkMultiSelect"
                @enable-select-all="startBulkSelectAll"
                @open-bulk-modal="openBulkModal"
                @disable-mode="disableBulkEditMode"
              />

              <b-button
                v-if="$can('ExportEersUsers', 'User')"
                @click="exportEersExcel()"
                variant="success"
                class="text-capitalize ml-1"
                type="button"
                :disabled="buttonExport"
                v-b-tooltip.hover
                title="Exportar usuarios EERS"
              >
                <feather-icon icon="DownloadCloudIcon" size="15" />
              </b-button>
              <b-button
                v-else-if="$can('export', 'User')"
                @click="exportExcel()"
                variant="success"
                class="text-capitalize ml-1"
                type="button"
                :disabled="buttonExport"
                v-b-tooltip.hover
                title="Exportar usuarios"
              >
                <feather-icon icon="DownloadCloudIcon" size="15" />
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="computedTableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
      >
        <!-- Column: bulkSelect (header) -->
        <template #head(bulkSelect)>
          <b-form-checkbox
            :checked="allVisibleSelected || bulkSelectAllMatching"
            @change="toggleSelectAllVisible"
            :indeterminate="someVisibleSelected && !allVisibleSelected && !bulkSelectAllMatching"
          />
        </template>

        <!-- Column: bulkSelect (cell) -->
        <template #cell(bulkSelect)="data">
          <b-form-checkbox
            :checked="isSelected(data.item.id)"
            @change="toggleRowSelection(data.item.id)"
            @click.native.stop
          />
        </template>

        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <template #head(actions)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(master_tenants)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(tenants)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(shared_tenants)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(types_alarms)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <!-- Column: User -->
        <template #cell(user)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.image"
                :text="avatarText(data.item.name + ' ' + data.item.last_name)"
                badge
                :badge-variant="data.item.online ? 'success' : 'danger'"
              />
            </template>
            <b-link class="font-weight-bold d-block">
              {{ data.item.name + " " + data.item.last_name }}
            </b-link>
            <small class="text-muted">{{ data.item.email }}</small>
            <br />
            <small class="text-muted">{{ data.item.username }}</small>
          </b-media>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button
            :id="`actions-${data.item.id}`"
            tabindex="0"
            variant="white"
            class="px-1 btn-y"
          >
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </b-button>
          <b-popover
            :target="`actions-${data.item.id}`"
            triggers="focus"
            placement="bottom"
            custom-class="popover-p-0"
            variant="white"
          >
            <b-list-group class="m-0">
              <!-- <b-list-group-item
                v-if="$can('update_cameras', 'User')"
                class="border-0"
                button
                @click="editUserCameras(data.item.id, data.item.email)"
              >
                <feather-icon icon="VideoIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $tc("camera.title", 2) }}
                </span>
              </b-list-group-item> -->

              <!-- <b-list-group-item v-if="$can('update_cameras', 'User')" class="border-0" button
                @click="editUserSharedTenant(data.item.id, data.item.email)">
                <feather-icon icon="VideoIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("users.shared_tenants") }}
                </span>
              </b-list-group-item> -->

              <b-list-group-item
                v-if="$can('create', 'User') && !data.item.verify_email && !data.item.active && data.item.email != '' && data.item.email.includes('@') && !data.item.email.match(/^[^@]*@\d+$/)"
                class="border-0"
                button
                @click="resendEmailVerication(data.item.id)"
              >
                <feather-icon icon="SendIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  Resend email to set password
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('reset_password', 'User')"
                class="border-0"
                button
                v-b-modal.reset-password
                @click="userEmail = data.item.email != '' ? data.item.email : data.item.username"
              >
                <feather-icon icon="LockIcon" />
                <span class="align-middle ml-50 text-capitalize">
                </span>
                  {{ data.item.email != '' ? $t("users.reset_password") : $t("users.reset_password_no_email") }}
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('update', 'User')"
                class="border-0"
                button
                @click="openEdit(data.item)"
                data-cy="automatest-edit-user-btn"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.edit") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('create', 'User')"
                class="border-0"
                button
                @click="openClone(data.item)"
              >
                <feather-icon icon="CopyIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.clone") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('delete', 'User')"
                class="border-0"
                button
                @click="confirmDelete(data.item.id)"
                data-cy="automatest-delete-user-btn"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.delete") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('read', 'ExternalToken')"
                class="border-0"
                button
                @click="externalToken(data.item.id)"
              >
                <feather-icon icon="KeyIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $tc("external_token.title", 2) }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('read', 'ExternalUser')"
                class="border-0"
                button
                @click="externalUser(data.item.id)"
              >
                <feather-icon icon="UsersIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $tc("external_user.title", 2) }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('create_qr_app', 'AccountSettings')"
                class="border-0"
                button
                @click="openQrModal(data.item.id)" 
              >
                <feather-icon icon="SmartphoneIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("account_settings.mobile_app.title") }}
                </span>
              </b-list-group-item>
            </b-list-group>
          </b-popover>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
              data-cy="pagination"
            >
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Bulk Edit Wizard Modal (mirrors the user wizard steps) -->
    <bulk-edit-wizard-modal
      :is-open="isBulkModalOpen"
      :form-data="bulkFormData"
      :selected-count="bulkSelectedCount"
      :is-submitting="isBulkSubmitting"
      :catalog-options="bulkCatalogOptions"
      @close="closeBulkModal"
      @submit="submitBulkEdit"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormCheckbox,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
  BMedia,
  BAvatar,
  BLink,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed, watch } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import Wizard from "./wizard/Wizard.vue";
import Filters from "./Filters.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { avatarText } from "@core/utils/filter";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import router from "@/router";
import Pagination from "@/components/Pagination.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import axios from "@/libs/axios";
import AccountSettingMobileApp from '@/views/user_profile/AccountSettingMobileApp.vue'

import BulkEditButton from "@/components/BulkEdit/BulkEditButton.vue";
import BulkEditWizardModal from "./BulkEditWizardModal.vue";
import FacetedSearch from "@/components/FacetedSearch.vue";
import useBulkEdit from "@/composables/useBulkEdit";
import {
  BULK_EDIT_FIELDS,
  BULK_EDIT_ENDPOINT,
  BULK_EDIT_BATCH_STATUS_ENDPOINT,
  BULK_EDIT_IDS_KEY,
} from "./bulkEditConfig";

export default {
  components: {
    Wizard,
    Sidenav,
    Filters,
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormCheckbox,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BMedia,
    BAvatar,
    BLink,
    vSelect,
    BreadcrumbMenu,
    FilterModal,
    ButtonFilters,
    AccountSettingMobileApp,
    BulkEditButton,
    BulkEditWizardModal,
    FacetedSearch,
  },
  computed: {
    searchOptions() {
      return [
        {
          label: this.$t('users.search_filters.soeid'),
          value: 'user',
          isArray: false,
        },
        {
          label: this.$t('users.search_filters.email'),
          value: 'email',
          isArray: false,
        },
        {
          label: this.$t('users.search_filters.name'),
          value: 'name',
          isArray: false,
        },
        {
          label: this.$t('users.search_filters.first_name'),
          value: 'first_name',
          isArray: false,
        },
        {
          label: this.$t('users.search_filters.second_name'),
          value: 'second_name',
          isArray: false,
        },
        {
          label: this.$t('users.search_filters.phone_number'),
          value: 'phone_number',
          isArray: false,
        },
      ];
    },
  },
  setup(_, context) {
    const isSidebarActiveByChatbot = store.getters["chatbot/getIsSidebarActive"];
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination
    const isSidebarActive = ref(isSidebarActiveByChatbot);
    const itemEdit = ref({});
    const isAdd = ref(isSidebarActiveByChatbot);
    const isWizardActive = ref(isSidebarActiveByChatbot);
    const itemCloned = ref({});
    const isClone = ref(false);
    const idItemOriginal = ref(null);
    const selectedUserId = ref(null)
    const loadQrUser = ref(null)

    const openEdit = (item) => {
      //isSidebarActive.value = true;
      itemEdit.value = item;
      isAdd.value = false;
      isWizardActive.value = true;
      isClone.value = false;
    };
    const { t } = useI18nUtils();

    const trans = {
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
    };

    const toast = useToast();
    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      masterTenantOptions,
      masterTenantFilter,
      tenantOptions,
      tenantFilter,
      profileOptions,
      profileFilter,
      permissionOptions,
      typeAlarmOptions,
      countryOptions,
      action,
      buttonExport,
      onlineFilter,
      clearSearchFilters,
      filtersCount,
      buildFilters,
      setFacetedFilters,
    } = useList();

    // ==================== Bulk Edit ====================
    // Active list filters (same keys fetchList sends) placed in the bulk
    // payload `filters` so select_all targets exactly what the list shows.
    const buildActiveFilters = () => {
      const candidates = {
        q: searchQuery.value,
        id_master_tenant: masterTenantFilter.value,
        id_tenant: tenantFilter.value,
        id_profile: profileFilter.value,
        online: onlineFilter.value,
      };
      const active = {};
      Object.entries(candidates).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          active[key] = value;
        }
      });
      return active;
    };

    const bulkEdit = useBulkEdit({
      endpoint: BULK_EDIT_ENDPOINT,
      fields: BULK_EDIT_FIELDS,
      idsKey: BULK_EDIT_IDS_KEY,
      batchStatusEndpoint: BULK_EDIT_BATCH_STATUS_ENDPOINT,
      getSelectAllExtras: buildActiveFilters,
      onSuccess: () => {
        refetchData();
      },
    });

    watch(
      totalList,
      (n) => {
        bulkEdit.totalCount.value = n || 0;
      },
      { immediate: true }
    );

    // App modules catalog (same fetch PermissionStep uses).
    const appModuleOptions = ref([]);
    store.dispatch("app_modules/getAll").then(({ data }) => {
      appModuleOptions.value = data.map((appModule) => ({
        value: appModule.id,
        label: appModule.name,
      }));
    });

    // Tenants narrowed by the master tenants picked in the bulk form (same
    // rule as TenantStep); with none picked, every tenant is offered.
    const bulkTenantOptions = computed(() => {
      const selectedMasters = bulkEdit.formData.value.master_tenant;
      const allTenants = store.getters["tenant/getItems"].map((tenant) => ({
        ...tenant,
        value: tenant.id,
        label: tenant.name,
      }));
      if (Array.isArray(selectedMasters) && selectedMasters.length) {
        return allTenants.filter((tenant) =>
          selectedMasters.includes(tenant.id_master_tenant)
        );
      }
      return allTenants;
    });

    const bulkCatalogOptions = computed(() => ({
      profileOptions: profileOptions.value,
      countryOptions: countryOptions.value,
      masterTenantOptions: masterTenantOptions.value,
      tenantOptions: bulkTenantOptions.value,
      typeAlarmOptions: typeAlarmOptions.value,
      appModuleOptions: appModuleOptions.value,
    }));

    const computedTableColumns = computed(() => {
      if (bulkEdit.isBulkEditMode.value) {
        return [
          { key: "bulkSelect", label: "", sortable: false, thStyle: "width: 40px" },
          ...tableColumns,
        ];
      }
      return tableColumns;
    });

    const startBulkMultiSelect = () => {
      bulkEdit.clearSelection();
      bulkEdit.enableBulkEditMode();
    };

    const startBulkSelectAll = () => {
      bulkEdit.enableBulkEditMode();
      bulkEdit.enableSelectAllMatching();
    };

    const visibleItems = computed(() => {
      return refListTable.value?.localItems || [];
    });

    const allVisibleSelected = computed(() => {
      const visible = visibleItems.value;
      if (!visible.length) return false;
      return visible.every(item => bulkEdit.isSelected(item.id));
    });

    const someVisibleSelected = computed(() => {
      const visible = visibleItems.value;
      if (!visible.length) return false;
      return visible.some(item => bulkEdit.isSelected(item.id));
    });

    const toggleSelectAllVisible = () => {
      if (bulkEdit.selectAllMatching.value) {
        bulkEdit.clearSelection();
        return;
      }
      const visible = visibleItems.value;
      if (allVisibleSelected.value) {
        visible.forEach(item => {
          if (bulkEdit.isSelected(item.id)) {
            bulkEdit.toggleItem(item.id);
          }
        });
      } else {
        visible.forEach(item => {
          if (!bulkEdit.isSelected(item.id)) {
            bulkEdit.toggleItem(item.id);
          }
        });
      }
    };

    // Row checkbox. If "select all matching" is active and one row gets
    // unchecked, exit that mode keeping the remaining visible rows selected.
    const toggleRowSelection = (id) => {
      if (bulkEdit.selectAllMatching.value) {
        const remaining = visibleItems.value
          .map(item => item.id)
          .filter(itemId => itemId !== id);
        bulkEdit.setSelectedIds(remaining);
        return;
      }
      bulkEdit.toggleItem(id);
    };
    // ====================================================

    const editUserCameras = function (id, email) {
      router.push({
        name: "user_cameras",
        params: { id_user: id, email: email },
      });
    };

    const editUserSharedTenant = function (id, email) {
      router.push({
        name: "user_shared_tenant",
        params: { id_user: id, email: email },
      });
    };

    const closeWizard = function () {
      isWizardActive.value = false;
      store.dispatch("camera_proxy/clearAll");
    };

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: trans["messages.delete.title"],
        text: trans["messages.delete.body"],
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: trans["actions.delete"],
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("user/delete", id)
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: response.message,
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 10000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    const externalToken = function (id) {
      router.push({ path: `/external_token/${id}` });
    };

    const externalUser = function (id) {
      router.push({ path: `/external_user/${id}` });
    };

    const exportExcel = () => {
      action.value = "export";
      buttonExport.value = true;
      fetchList();
    };

    const exportEersExcel = async () => {
      try {
        buttonExport.value = true;
        const responseType = "blob";
        const response = await axios.get('v1/users-eers/export', { responseType });

        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );

        const filename = response.headers["content-disposition"].split("filename=")[1];

        const link = document.createElement("a");

        link.href = url;

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        const notification = {
          id: filename,
          progress: 100,
          completed: true,
          view: "users",
          url: url,
          download: filename,
          title: "Reporte Forms. Seguridad operativa",
          body: "Descarga completa",
          error: false,
          timestamp:
            new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString(),
        };

        store.dispatch("grid/updateDownloadNotification", notification);
      } catch (error) {
        const { statusText } = error.response;

        toast({
          component: ToastificationContent,
          props: {
            title: statusText,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        buttonExport.value = false;
      }
    }

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
      isClone.value = true;
    };

    const openClone = (item) => {
      idItemOriginal.value = item.id;

      itemCloned.value = { ...item };
      itemCloned.value.id = null;
      itemCloned.value.username = null;


      isClone.value = true;
      isAdd.value = true;
      isWizardActive.value = true;
    };

    const openWizard = () => {
      isAdd.value = true;
      isWizardActive.value = true;
    };

    const resendEmailVerication = function (id) {
      store
        .dispatch("user/resendEmailVerication", id)
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            self.refetchData();
          } else {
            toast(
              {
                component: ToastificationContent,
                props: {
                  title: response.message,
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              },
              {
                timeout: 10000,
              }
            );
          }
        })
        .catch((response) => {
          toast(
            {
              component: ToastificationContent,
              props: {
                title: response.response.data.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            },
            {
              timeout: 10000,
            }
          );
        });
    };

    const url = ref('')
    const userEmail = ref('')
    const btnDisabled = ref(false)
    const generateLink = (data) => {
      btnDisabled.value = true
      store
        .dispatch("user/generateLink", userEmail.value)
        .then((response) => {
          if(response.success){
            url.value = response.data.url;
            btnDisabled.value = false
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
          }
        })
        .catch((response) => {
          toast(
            {
              component: ToastificationContent,
              props: {
                title: response.response.data.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            },
            {
              timeout: 10000,
            }
          );
        });
    }

    const requestLinkemail = () => {
      btnDisabled.value = true
      store
        .dispatch("user/requestLinkemail", userEmail.value)
        .then((response) => {
          if (response.success) {
            btnDisabled.value = false
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
          }
        })
        .catch((response) => {
          toast(
            {
              component: ToastificationContent,
              props: {
                title: response.response.data.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            },
            {
              timeout: 10000,
            }
          );
        });
    }

    const onCopy = () => {
      navigator.clipboard.writeText(url.value);
      toast({
        component: ToastificationContent,
        props: {
          title: 'Copiado correctamente',
          icon: 'ClipboardIcon',
          variant: 'success',
        },
      })
    };

    const onError = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: 'No se pudo copiar',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    };

    const openQrModal = (userId) => {
      selectedUserId.value = userId
      loadQrUser.value.show()
    };

    return {
      update,
      isWizardActive,
      isSidebarActive,
      isAdd,
      itemEdit,
      itemCloned,
      idItemOriginal,
      isClone,
      openEdit,
      openAdd,
      openClone,
      confirmDelete,
      avatarText,
      editUserCameras,
      editUserSharedTenant,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      masterTenantOptions,
      masterTenantFilter,
      tenantOptions,
      tenantFilter,
      profileOptions,
      profileFilter,
      permissionOptions,
      typeAlarmOptions,
      externalToken,
      externalUser,
      countryOptions,
      exportExcel,
      exportEersExcel,
      buttonExport,
      onlineFilter,
      openWizard,
      closeWizard,
      clearSearchFilters,
      filtersCount,
      buildFilters,
      setFacetedFilters,
      resendEmailVerication,
      url,
      userEmail,
      generateLink,
      requestLinkemail,
      btnDisabled,
      onCopy,
      onError,
      loadQrUser,
      openQrModal,
      selectedUserId,

      // Bulk Edit
      isBulkEditMode: bulkEdit.isBulkEditMode,
      bulkSelectedCount: bulkEdit.selectedCount,
      bulkHasSelection: bulkEdit.hasSelection,
      bulkSelectAllMatching: bulkEdit.selectAllMatching,
      toggleBulkEditMode: bulkEdit.toggleBulkEditMode,
      disableBulkEditMode: bulkEdit.disableBulkEditMode,
      startBulkMultiSelect,
      startBulkSelectAll,
      openBulkModal: bulkEdit.openBulkModal,
      closeBulkModal: bulkEdit.closeBulkModal,
      submitBulkEdit: bulkEdit.submitBulkEdit,
      isBulkModalOpen: bulkEdit.isBulkModalOpen,
      bulkFormData: bulkEdit.formData,
      isBulkSubmitting: bulkEdit.isSubmitting,
      bulkCatalogOptions,
      computedTableColumns,
      toggleItem: bulkEdit.toggleItem,
      toggleRowSelection,
      isSelected: bulkEdit.isSelected,
      allVisibleSelected,
      someVisibleSelected,
      toggleSelectAllVisible,
    };
  },
};
</script>

<style scoped lang="scss">
.user-search-actions {
  flex-wrap: wrap;
  row-gap: 0.5rem;
}

.user-search-input {
  flex: 1 1 0;
  min-width: 190px;
}
</style>
